import React, { useEffect, useState } from 'react'
import SimpleModal from 'components/SimpleModal'
import { CustomButton, SimpleTitleCard } from 'styles/global'
import UploadIcon from 'assets/images/upload-icon.png'
import { Col, Label, Row } from 'reactstrap'
import CustomSelect from 'components/Select'
import { UploadArquivo } from 'components/UploadArquivo'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import { SIMULACAO } from 'store/modules/simulacao/types'
import * as simulacaoActions from 'store/modules/simulacao/actions'
import useRedux from 'hooks/useRedux'
import {
  DefaultExtensionType,
  defaultStyles,
  FileIcon,
  FileIconProps
} from 'react-file-icon'
import { filesize } from 'filesize'
import { ArquivosAnexadosCrefaz } from 'views/simulacao/style'
import { FiTrash } from 'react-icons/fi'
import { toast } from 'react-toastify'

interface Props {
  isOpen: boolean
  toggle: () => void
}

const ModalUploadCrefaz = ({ isOpen, toggle }: Props) => {
  const { arquivosCrefaz, optionsSelect } = useSelector<
    ApplicationState,
    SIMULACAO
  >((state) => state.simulacao)

  const [arquivo, setArquivo] = useState<File | null>(null)
  const [tipoDocumento, setTipoDocumento] = useState<string | null>(null)

  const { dispatch } = useRedux()

  function handleAddArquivo(file: File) {
    if (file) setArquivo(file)
  }

  function cleanArquivo() {
    setArquivo(null)
  }

  function selectTipoDocumento(tipo: string | null) {
    setTipoDocumento(tipo)
  }

  function cleanTipoDocumento() {
    setTipoDocumento(null)
  }

  function getExtension(fileName: string) {
    return (fileName.split('.').pop() || '') as keyof Record<
      DefaultExtensionType,
      Partial<FileIconProps>
    >
  }

  const handleEnvioArquivo = () => {
    if (!arquivo || !tipoDocumento) {
      toast.warning('Selecione o tipo de documento antes de enviar.')
      return
    }

    const documentoExistente = arquivosCrefaz.some(
      (arquivo) => arquivo.tipoArquivo === tipoDocumento
    )

    if (documentoExistente) {
      toast.error('Já existe um arquivo deste tipo anexado.')
      return
    }

    dispatch(
      simulacaoActions.addArquivoCrefaz({
        uploadedFile: arquivo,
        tipoArquivo: tipoDocumento
      })
    )
    cleanArquivo()
    cleanTipoDocumento()
  }

  const avancar = () => {
    const tiposObrigatorios = [
      '58F67D69-1D3B-4B59-8D6C-653DD83EE292',
      'F0C0771F-F9F6-4C1C-B86B-ADC1A356CB8A'
    ]

    const tiposAnexados = arquivosCrefaz.map((arquivo) =>
      arquivo.tipoArquivo.toUpperCase()
    )

    const tiposFaltando = tiposObrigatorios.filter(
      (tipoObrigatorio) =>
        !tiposAnexados.includes(tipoObrigatorio.toUpperCase())
    )

    const descricoesFaltando = tiposFaltando.map((tipoFaltando) => {
      const tipo = optionsSelect.tiposDocumentoCrefaz.find(
        (option) => option.id.toUpperCase() === tipoFaltando
      )
      return tipo?.descricao || ''
    })

    if (descricoesFaltando.length > 0) {
      toast.error(
        `Faltam os seguintes documentos: ${descricoesFaltando.join(', ')}`
      )
      return
    }

    dispatch(simulacaoActions.uploadArquivosCrefazSaga())
  }

  useEffect(() => {
    if (isOpen) {
      if (optionsSelect.tiposDocumentoCrefaz.length === 0) {
        dispatch(simulacaoActions.getTiposDocumentoCrefazSaga())
      }
    }
  }, [isOpen, dispatch, optionsSelect.tiposDocumentoCrefaz])

  return (
    <SimpleModal
      toggle={() => toggle()}
      isOpen={isOpen}
      size="lg"
      header={
        <SimpleTitleCard>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center gap-2">
              <img src={UploadIcon} alt="Ícone" />
              Anexe os documentos abaixo para continuar com a contratação:
            </div>
            <ul className="m-0">
              <li>A fatura;</li>
              <li>O documento de identificação do cliente;</li>
            </ul>
          </div>
        </SimpleTitleCard>
      }
      dataBackdrop="static"
    >
      <div className="d-flex flex-column gap-3">
        <Row>
          <Col>
            <Label className="label-12">Tipo de documento</Label>
            <CustomSelect
              options={optionsSelect.tiposDocumentoCrefaz}
              value={tipoDocumento}
              onChange={(e) => {
                selectTipoDocumento(e)
              }}
              accessorLabel="descricao"
              accessorValue="id"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <UploadArquivo
              onChange={(e) => {
                if (e instanceof File) handleAddArquivo(e)
              }}
              invalid={false}
              acceptedFiles={{
                'image/png': ['.png'],
                'image/jpeg': ['.jpeg', '.jpg'],
                'application/pdf': ['.pdf']
              }}
              message="Somente arquivos PNG, JPEG e PDF"
              value={arquivo}
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-evenly flex-wrap gap-3">
          <CustomButton
            background="var(--verde-riber)"
            onClick={handleEnvioArquivo}
            disabled={!arquivo || !tipoDocumento}
          >
            Clique para enviar
          </CustomButton>

          <CustomButton background="var(--roxo-riber)" onClick={avancar}>
            Avançar
          </CustomButton>
        </div>
        {arquivosCrefaz.length > 0 && (
          <ArquivosAnexadosCrefaz>
            <Label className="label-12">Arquivos anexados</Label>
            <div className="d-flex flex-column gap-4">
              {arquivosCrefaz.map((arquivo, i) => (
                <div
                  key={`arquivo-${i}`}
                  className="d-flex justify-content-between align-items-center"
                >
                  <label className="preview">
                    <div className="icon">
                      <FileIcon
                        extension={
                          typeof arquivo === 'string'
                            ? 'base64'
                            : getExtension(arquivo.uploadedFile?.name ?? '')
                        }
                        {...(typeof arquivo === 'string'
                          ? {}
                          : defaultStyles[
                              getExtension(arquivo.uploadedFile?.name ?? '')
                            ])}
                      />
                    </div>
                    <span>
                      {typeof arquivo === 'string'
                        ? 'base64'
                        : filesize(arquivo.uploadedFile?.size ?? 0)}
                    </span>
                    {typeof arquivo === 'string'
                      ? 'Arquivo em base64'
                      : arquivo.uploadedFile?.name}
                  </label>
                  <label className="d-flex align-items-center justify-content-center gap-3">
                    <span className="text-center">
                      {
                        optionsSelect.tiposDocumentoCrefaz.find(
                          (tipo) => tipo.id === arquivo.tipoArquivo
                        )?.descricao
                      }
                    </span>
                    <FiTrash
                      className="pointer"
                      onClick={() => {
                        dispatch(simulacaoActions.removeArquivoCrefaz(i))
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          </ArquivosAnexadosCrefaz>
        )}
      </div>
    </SimpleModal>
  )
}

export default ModalUploadCrefaz
